import { Clock, RotateCcw, User, Eye, GitCompare } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useConfirmDialog } from '@/hooks'
import {
  useVersions,
  useVersion,
  useRestoreVersion,
  type DocumentVersion,
} from '@/hooks'
import { cn } from '@/lib/utils'

interface VersionHistoryProps {
  documentId: string
  className?: string
}

/**
 * 版本历史组件
 * 显示文档的所有历史版本，支持查看和恢复
 */
export function VersionHistory({ documentId, className }: VersionHistoryProps) {
  const { data: versions, isLoading } = useVersions(documentId)
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null)
  const [compareVersion, setCompareVersion] = useState<number | null>(null)

  if (isLoading) {
    return <VersionHistorySkeleton />
  }

  if (!versions || versions.length === 0) {
    return (
      <div className={cn('flex flex-col items-center justify-center py-12 text-muted-foreground', className)}>
        <Clock className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-lg font-medium">暂无版本历史</p>
        <p className="text-sm mt-1">文档保存后将自动创建版本</p>
      </div>
    )
  }

  return (
    <div className={cn('flex h-full', className)}>
      {/* Version List */}
      <div className="w-72 border-r overflow-auto">
        <div className="p-4 border-b bg-muted/30">
          <h3 className="font-medium">版本历史</h3>
          <p className="text-sm text-muted-foreground mt-1">
            共 {versions.length} 个版本
          </p>
        </div>
        <div className="divide-y">
          {versions.map((version) => (
            <VersionItem
              key={version.id}
              version={version}
              isSelected={selectedVersion === version.versionNumber}
              isComparing={compareVersion === version.versionNumber}
              onSelect={() => setSelectedVersion(version.versionNumber)}
              onCompare={() => {
                if (compareVersion === version.versionNumber) {
                  setCompareVersion(null)
                } else {
                  setCompareVersion(version.versionNumber)
                }
              }}
            />
          ))}
        </div>
      </div>

      {/* Version Detail / Compare View */}
      <div className="flex-1 overflow-auto">
        {selectedVersion && compareVersion ? (
          <VersionCompare
            documentId={documentId}
            version1={Math.min(selectedVersion, compareVersion)}
            version2={Math.max(selectedVersion, compareVersion)}
          />
        ) : selectedVersion ? (
          <VersionDetail
            documentId={documentId}
            versionNumber={selectedVersion}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <div className="text-center">
              <Eye className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>选择一个版本查看详情</p>
              <p className="text-sm mt-1">或选择两个版本进行对比</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ==================== Sub Components ====================

interface VersionItemProps {
  version: DocumentVersion
  isSelected: boolean
  isComparing: boolean
  onSelect: () => void
  onCompare: () => void
}

function VersionItem({
  version,
  isSelected,
  isComparing,
  onSelect,
  onCompare,
}: VersionItemProps) {
  return (
    <div
      className={cn(
        'p-3 cursor-pointer transition-colors',
        isSelected && 'bg-primary/10',
        isComparing && 'bg-yellow-500/10',
        !isSelected && !isComparing && 'hover:bg-muted/50'
      )}
      onClick={onSelect}
    >
      <div className="flex items-center justify-between">
        <span className="font-medium">版本 {version.versionNumber}</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={(e) => {
            e.stopPropagation()
            onCompare()
          }}
          title={isComparing ? '取消对比' : '添加到对比'}
        >
          <GitCompare className={cn('h-4 w-4', isComparing && 'text-yellow-500')} />
        </Button>
      </div>
      <div className="mt-1 text-sm text-muted-foreground">
        <div className="flex items-center gap-1">
          <User className="h-3 w-3" />
          {version.author?.name ?? '未知'}
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          <Clock className="h-3 w-3" />
          {formatDateTime(version.createdAt)}
        </div>
      </div>
    </div>
  )
}

interface VersionDetailProps {
  documentId: string
  versionNumber: number
}

function VersionDetail({ documentId, versionNumber }: VersionDetailProps) {
  const { data: version, isLoading } = useVersion(documentId, versionNumber)
  const restoreVersion = useRestoreVersion()
  const { confirmWithPreset, ConfirmDialog, setLoading } = useConfirmDialog()

  const handleRestore = async () => {
    const confirmed = await confirmWithPreset(
      'restore',
      `确定要恢复到版本 ${versionNumber} 吗？这将创建一个新版本。`
    )
    if (confirmed) {
      setLoading(true)
      try {
        await restoreVersion.mutateAsync({ documentId, versionNumber })
      } finally {
        setLoading(false)
      }
    }
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4 mt-2" />
      </div>
    )
  }

  if (!version) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        版本不存在
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between border-b p-4">
        <div>
          <h3 className="font-medium">版本 {version.versionNumber}</h3>
          <p className="text-sm text-muted-foreground">
            {version.author?.name ?? '未知'} · {formatDateTime(version.createdAt)}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void handleRestore()}
          disabled={restoreVersion.isPending}
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          恢复此版本
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <h2 className="text-xl font-bold mb-4">{version.title}</h2>
        <div className="prose prose-sm max-w-none dark:prose-invert whitespace-pre-wrap font-mono text-sm bg-muted/30 p-4 rounded-lg">
          {version.content}
        </div>
      </div>

      <ConfirmDialog />
    </div>
  )
}

interface VersionCompareProps {
  documentId: string
  version1: number
  version2: number
}

function VersionCompare({ documentId, version1, version2 }: VersionCompareProps) {
  const { data: v1 } = useVersion(documentId, version1)
  const { data: v2 } = useVersion(documentId, version2)

  if (!v1 || !v2) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  // 简单的行级对比
  const lines1 = v1.content.split('\n')
  const lines2 = v2.content.split('\n')
  const maxLines = Math.max(lines1.length, lines2.length)

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex border-b">
        <div className="flex-1 p-3 border-r bg-red-500/5">
          <span className="font-medium">版本 {version1}</span>
          <span className="text-sm text-muted-foreground ml-2">
            {formatDateTime(v1.createdAt)}
          </span>
        </div>
        <div className="flex-1 p-3 bg-green-500/5">
          <span className="font-medium">版本 {version2}</span>
          <span className="text-sm text-muted-foreground ml-2">
            {formatDateTime(v2.createdAt)}
          </span>
        </div>
      </div>

      {/* Diff Content */}
      <div className="flex-1 overflow-auto">
        <div className="flex font-mono text-sm">
          <div className="flex-1 border-r">
            {Array.from({ length: maxLines }).map((_, i) => {
              const line = lines1[i] ?? ''
              const otherLine = lines2[i] ?? ''
              const isDiff = line !== otherLine
              return (
                <div
                  key={i}
                  className={cn(
                    'px-3 py-0.5 whitespace-pre-wrap',
                    isDiff && 'bg-red-500/10'
                  )}
                >
                  {line || '\u00A0'}
                </div>
              )
            })}
          </div>
          <div className="flex-1">
            {Array.from({ length: maxLines }).map((_, i) => {
              const line = lines2[i] ?? ''
              const otherLine = lines1[i] ?? ''
              const isDiff = line !== otherLine
              return (
                <div
                  key={i}
                  className={cn(
                    'px-3 py-0.5 whitespace-pre-wrap',
                    isDiff && 'bg-green-500/10'
                  )}
                >
                  {line || '\u00A0'}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

function VersionHistorySkeleton() {
  return (
    <div className="flex h-full">
      <div className="w-72 border-r">
        <div className="p-4 border-b">
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-4 w-16 mt-1" />
        </div>
        <div className="divide-y">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-3">
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-4 w-24 mt-1" />
              <Skeleton className="h-4 w-32 mt-0.5" />
            </div>
          ))}
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center">
        <Skeleton className="h-12 w-12 rounded-full" />
      </div>
    </div>
  )
}

// ==================== Helpers ====================

function formatDateTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}
